import React, { useEffect, useRef, useState } from 'react';
import { RichTreeView, TreeItem2, TreeItem2LabelInput, useTreeItem2Utils } from '@mui/x-tree-view';
import { css } from '@emotion/css';
import { optionallyRenderIcon } from './renderIcon';
import { FONT_WEIGHT_BOLD } from './theme';
import { styled } from '@mui/material';
import ResourceTreeProvider from '../core-providers/ResourceTreeProvider';
import WithContextMenu from './WithContextMenu';
import { ActionHandlerBuilder } from '../core-providers/actions';
const strongStyle = css `
  font-weight: ${FONT_WEIGHT_BOLD};
`;
const TreeItemLabelInput = styled(TreeItem2LabelInput, {
    name: 'CustomTreeItemLabelInput',
    slot: 'LabelInput',
    overridesResolver: (_, styles) => styles.labelInput,
})(({ theme }) => ({
    ...theme.typography.body1,
    color: theme.palette.text.primary,
}));
const TreeItem = ({ itemId, children }) => {
    const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
    const [renameAction, setRenameAction] = useState(null);
    const { interactions, publicAPI, status } = useTreeItem2Utils({ itemId, children });
    const item = publicAPI.getItem(itemId);
    const onActionInProgress = ActionHandlerBuilder.new().with('rename', (action) => {
        setRenameAction(action);
        if (!status.editing)
            interactions.toggleItemEditing();
    }).toHandler();
    const onActionFinalized = ActionHandlerBuilder.new()
        .with('rename', setRenameAction.bind(null, null))
        .toHandler();
    // MUI's RichTreeView label editing feature is currently experimental.
    // The functions below assume direct control of TreeItem2LabelInput focus and
    // the input-completion events of blur and keydown.
    //
    // This is done to circumvent focus-thrashing when the input is activated from
    // the context menu, and to simplify interception of a completed input for the
    // in-progress rename action.
    const labelInputRef = useRef(null);
    useEffect(() => {
        labelInputRef.current?.select();
    }, [labelInputRef, isContextMenuOpen]);
    const onInputBlur = (event) => {
        const value = event.target.value;
        event.defaultMuiPrevented = true;
        if (isContextMenuOpen)
            return; // Discard initial blur from clicking the open context menu
        if (value === undefined || value === item.label) {
            return interactions.handleCancelItemLabelEditing(event);
        }
        renameAction?.run(value);
        interactions.handleSaveItemLabel(event, value);
    };
    const onInputKeyDown = (event) => {
        const value = event.target.value;
        event.defaultMuiPrevented;
        if (event.key === 'Enter' && value !== undefined && value !== item.label) {
            renameAction?.run(value);
            return interactions.handleSaveItemLabel(event, value);
        }
        if (event.key === 'Escape') {
            return interactions.handleCancelItemLabelEditing(event);
        }
    };
    return (React.createElement(WithContextMenu, { actions: item.actions, onActionInProgress: onActionInProgress, onActionFinalized: onActionFinalized, openedClassName: css `outline: 1px solid rgb(128,128,128);`, onOpenChanged: setIsContextMenuOpen },
        React.createElement(TreeItem2, { id: item.id, className: item.styledStrong ? strongStyle : undefined, itemId: item.id, label: item.label, slots: {
                icon: item.icon ? () => optionallyRenderIcon(item.icon, { fontSize: 'small' }) : undefined,
                labelInput: TreeItemLabelInput,
            }, slotProps: {
                labelInput: {
                    ref: labelInputRef,
                    onBlur: onInputBlur,
                    onKeyDown: onInputKeyDown,
                }
            } }, children)));
};
const ResourceTreeView = ({ className }) => {
    const treeModel = ResourceTreeProvider.provideTreeModel();
    const items = treeModel.tree();
    const selectedItem = treeModel.selectedItem();
    return (React.createElement("div", { id: "resource-tree", className: className },
        React.createElement(RichTreeView, { items: items, isItemEditable: (item) => Boolean(item.editable), experimentalFeatures: { labelEditing: true }, slots: { item: TreeItem }, selectedItems: selectedItem?.id || null, onSelectedItemsChange: (_, itemId) => itemId && treeModel.itemById(itemId)?.onSelect() })));
};
export default ResourceTreeView;
//# sourceMappingURL=ResourceTreeView.js.map